import { action, observable, runInAction, when } from 'mobx';
import { nanoid } from 'nanoid';

import type {
  BaseErrorCodes,
  GameState,
  GameStateCompressed,
  HostAllowPlayerToJoinMessage,
  PlayerData,
  PlayerPeerMetadata,
  PlayerSendInteractiveQuestionAnswerMessage,
  PlayerSendScoreableInteractiveQuestionAnswerMessage,
  PlayP2pHostMessage,
  PlayP2pPlayerMessage,
  PlayWebsocketClientToServerGameFinished,
  PlayWebsocketServerToClientMessage,
} from '@genially/p2p-lib';
import {
  isGameState,
  PlayErrorCodes,
  PlayP2pHostMessageType,
  PlayP2pPlayerMessageType,
  PlayServerDisconnectReason,
  PlayUserType,
  PlayWebsocketClientToServerMessageType,
} from '@genially/p2p-lib';
import { decodeError } from '@genially/ui/build/utils/decodeError';
import { FEATURE_FLAGS } from '@genially/ui/src/constants/featureFlags';
import {
  addJitterToTime,
  calculate,
  MeasurePerformanceService,
  sleep,
} from '@genially/utils';

import { createLogger, createOnlyRemoteLogger, LoggerLevel } from '../../../util/logger';
import { PeerSyncMessageTimeoutError } from '../errors/PeerSyncMessageTimeoutError';
import type { GameStateModel } from '../models/GameStateModel';
import { PlayP2pClient, type PlayP2pClientEmittedEvents } from '../PlayP2pClient';
import { calculateStringSizeInKb } from './utils/calculateStringSizeInKb';
import { GameConfigDebugConfig } from './utils/GameConfigDebugConfig';

export enum HostPlayP2pClientEventType {
  PlayerJoined = 'player-joined',
  PlayersJoined = 'players-joined',
  PlayerDisconnected = 'player-disconnected',
  PlayersDisconnected = 'players-disconnected',
  GameChanged = 'game-changed',
  GameSync = 'game-sync',
  Joined = 'joined',
  InteractiveQuestionResolved = 'interactive-question-resolved',
  InteractiveQuestionAnswerReceived = 'interactive-question-answer-received',
  HostReplaced = 'host-replaced',
  ToggleOpenAnswerVisibility = 'toggle-open-answer-visibility',
}

export type HostPlayP2pClientEmittedEvents = PlayP2pClientEmittedEvents<{
  [HostPlayP2pClientEventType.PlayerJoined]: [
    { joinedPlayer: PlayerData; allPlayers: PlayerData[] },
  ];
  [HostPlayP2pClientEventType.PlayersJoined]: [
    { joinedPlayers: PlayerData[]; allPlayers: PlayerData[] },
  ];
  [HostPlayP2pClientEventType.PlayerDisconnected]: [
    { disconnectedPlayerId: string; allPlayers: PlayerData[] },
  ];
  [HostPlayP2pClientEventType.PlayersDisconnected]: [
    { disconnectedPlayers: string[]; allPlayers: PlayerData[] },
  ];
  [HostPlayP2pClientEventType.GameChanged]: [GameState];
  [HostPlayP2pClientEventType.GameSync]: [Promise<void>];
  [HostPlayP2pClientEventType.Joined]: [void];
  [HostPlayP2pClientEventType.InteractiveQuestionResolved]: [
    { interactiveQuestionId: string; isLastScored?: boolean },
  ];
  [HostPlayP2pClientEventType.InteractiveQuestionAnswerReceived]: [];
  [HostPlayP2pClientEventType.HostReplaced]: [];
}>;

export type HostPlayP2pClientConstructorParams = {
  geniallyId: string;
  previousSessionId?: string;
  teamId: string;
  geniallyUserId: string;
  initialSlide: string;
  featureFlags: string[];
  playersLimitPerSession: number;
};

const MaxRetriesOnSessionIdInUseError = 3;

const LoggerCreationMetadata = {
  minLoggerLevel: LoggerLevel.Debug,
  prefix: 'HostPlayP2pClient',
  app: '@genially/play',
};

export class HostPlayP2pClient extends PlayP2pClient<
  HostPlayP2pClientEmittedEvents,
  PlayerPeerMetadata
> {
  protected readonly _logger = createLogger(LoggerCreationMetadata);

  protected readonly measurePerformanceService = new MeasurePerformanceService(
    'HostPlayP2pClient',
  );

  private numRetriesOnSessionIdInUseError = 0;

  public readonly teamId: string;

  public readonly geniallyUserId: string;

  private sendGameStatePatchesIntervalPid: number | undefined;

  private handlePendingPlayersToJoinIntervalPid: number | undefined;

  private playersLimitPerSession: number;

  constructor({
    geniallyId,
    teamId,
    geniallyUserId,
    previousSessionId,
    initialSlide,
    featureFlags,
    playersLimitPerSession,
  }: HostPlayP2pClientConstructorParams) {
    const deviceId = PlayP2pClient.generateDeviceId();
    const sessionId = previousSessionId ?? PlayP2pClient.generateSessionId();

    super({
      type: PlayUserType.Host,
      deviceId,
      sessionId,
      game: {
        isTrackedInLogRocket: false,
        currentSlide: initialSlide,
        isStarted: false,
        isFinished: false,
        startedAt: undefined,
        finishedAt: undefined,
        isSessionBlocked: false,
        version: 0,
        interactiveQuestionAnswers: {},
      },
      geniallyId,
      featureFlags,
      playersLimitPerSession,
    });

    this.teamId = teamId;
    this.geniallyUserId = geniallyUserId;
    this.playersLimitPerSession = playersLimitPerSession;

    const remoteLogger = createOnlyRemoteLogger(LoggerCreationMetadata);

    this.measurePerformanceService.init(
      (message, metadata) =>
        remoteLogger.debug(message, {
          ...metadata,
          teamId: this.teamId,
          geniallyUserId: this.geniallyUserId,
          sessionId: this.sessionId,
          geniallyId: this.geniallyId,
          deviceId: this.deviceId,
          type: this.type,
          pingTime: this.pingTime,
          peerId: this._peerId ?? 'undefined',
        }),
      {
        threshold: 5,
      },
    );

    this.measurePerformanceService.measureThis(this, {
      ignoredMethods: ['peerId', 'debugInfo', 'connection'],
    });

    this.sendGameStatePatchesIntervalPid = window.setInterval(
      this.sendGameStatePatches.bind(this),
      100,
    );

    this.disposers.push(
      when(
        () => this.game.isStarted,
        () => {
          if (this.isConnected) {
            this.sendGameStartWebsocketMessage();
          }
        },
      ),
    );

    this.disposers.push(
      when(
        () => this.game.isFinished,
        () => {
          if (this.isConnected) {
            this.sendGameFinishedWebsocketMessage();
            this.dispose();
          }
        },
      ),
    );
  }

  buildTransportParams(): Record<string, string> {
    return {
      ...super.buildTransportParams(),
      teamId: this.teamId,
      geniallyUserId: this.geniallyUserId,
    };
  }

  onPeerIdReceived() {
    // Do nothing
  }

  get logger() {
    this._logger.setMetadata({
      geniallyId: this.geniallyId,
      sessionId: this.sessionId,
      deviceId: this.deviceId,
      teamId: this.teamId,
      gameSize: calculateStringSizeInKb(JSON.stringify(this._game.asJS)),
      gameSizeCompressed: calculateStringSizeInKb(
        JSON.stringify(this.gameCompressor.compress(this._game.asJS)),
      ),
      geniallyUserId: this.geniallyUserId,
      currentPlayers: this.players.map(p => p.username).join(', '),
      currentAmountOfPlayers: this.players.length,
      sessionIsStarted: this._game.isStarted,
      sessionIsFinished: this._game.isFinished,
      sessionIsBlocked: this._game.isSessionBlocked,
      averageSyncResponseTime: this.getAverageSyncResponseTime(),
      pingTime: this.pingTime,
    });

    return this._logger;
  }

  get currentSlide(): string {
    return this._game.currentSlide;
  }

  get players(): PlayerData[] {
    return Array.from(this.connections.entries())
      .filter(([_, metadata]) => metadata.type === PlayUserType.Player)
      .map(([peerId, metadata]) => {
        const playerData: PlayerData = {
          id: peerId,
          username: metadata.username,
        };

        return playerData;
      });
  }

  get isPlayersLimitReached(): boolean {
    if (this.hasFeatureFlag(FEATURE_FLAGS.FEAT_LT4_PLAY_SKIP_SESSION_PLAYER_LIMITS)) {
      return false;
    }

    if (GameConfigDebugConfig.skipPlayerLimits) {
      return false;
    }

    return this.players.length === this.playersLimitPerSession;
  }

  public markAsTrackedInLogRocket() {
    this.logger.debug('Marked as tracked in LogRocket');
    this.game.setIsTrackedInLogRocket(true);
  }

  private pendingPlayersToJoin: Record<string, PlayerPeerMetadata> = {};

  private sendGameStatePatches(): void {
    if (!this.game.hasPendingPatches) return;

    this.sendToAllPlayers({
      type: PlayP2pHostMessageType.SendGameStatePatches,
      payload: this.game.getAndResetPatches(),
    });
  }

  protected async onPeerConnected(peerId: string, metadata: PlayerPeerMetadata) {
    return this.processingQueue.queue('onPeerConnected', async () => {
      if (!metadata) {
        this.logger.error(`Connection without metadata from ${peerId}`);
        return;
      }

      this.pendingPlayersToJoin[peerId] = metadata;

      if (this.handlePendingPlayersToJoinIntervalPid) {
        clearTimeout(this.handlePendingPlayersToJoinIntervalPid);
      }

      this.handlePendingPlayersToJoinIntervalPid = window.setTimeout(
        this.handlePendingPlayersToJoin.bind(this),
        500,
      );
    });
  }

  private sendGameFinishedWebsocketMessage() {
    const message: PlayWebsocketClientToServerGameFinished = {
      type: PlayWebsocketClientToServerMessageType.GameFinished,
      duration: this.game.sessionDuration!,
    };

    this.sendWebsocketMessage(message);
  }

  private handlePendingPlayersToJoin() {
    return this.processingQueue.queue('onPeerConnected', async () => {
      const peerIds = Object.keys(this.pendingPlayersToJoin);

      if (peerIds.length === 0) {
        return;
      }

      const playersPerPeerId = { ...this.pendingPlayersToJoin };
      this.pendingPlayersToJoin = {};

      const playersEntries = Object.entries(playersPerPeerId);

      const playersWithLessVersion = playersEntries
        .filter(([_, metadata]) => metadata.gameVersion < this._game.version)
        .map(([peerId]) => peerId);

      await this.sendGameStateToPlayers(playersWithLessVersion);

      const allowPlayerToJoinMessage: HostAllowPlayerToJoinMessage = {
        type: PlayP2pHostMessageType.AllowPlayerToJoin,
        payload: {
          featureFlags: this.featureFlags,
        },
      };

      this.sendToPlayers(peerIds, allowPlayerToJoinMessage);

      this.logger.debug(`${playersEntries.length} players joined`);

      this.emitter.emit(HostPlayP2pClientEventType.PlayersJoined, {
        joinedPlayers: playersEntries.map(([peerId, metadata]) => ({
          id: peerId,
          username: metadata.username,
        })),
        allPlayers: this.players,
      });
    });
  }

  protected async onSetPeers(playersPerPeerId: Record<string, PlayerPeerMetadata>) {
    return this.processingQueue.queue('onPeerConnected', async () => {
      await this.syncGameFromPlayers(playersPerPeerId);

      const peerIds = Object.keys(playersPerPeerId);

      const peersToSendGameState: string[] = [];
      const peersToAllowToJoin: string[] = [];

      peerIds.forEach(pId => {
        if (playersPerPeerId[pId].gameVersion < this._game.version) {
          peersToSendGameState.push(pId);
        }

        if (playersPerPeerId[pId].gameVersion === 0) {
          peersToAllowToJoin.push(pId);
        }
      });

      if (peersToSendGameState.length > 0) {
        await this.sendGameStateToPlayers(peersToSendGameState);
      }

      const allowPlayerToJoinMessage: HostAllowPlayerToJoinMessage = {
        type: PlayP2pHostMessageType.AllowPlayerToJoin,
        payload: {
          featureFlags: this.featureFlags,
        },
      };

      this.sendToPlayers(peersToAllowToJoin, allowPlayerToJoinMessage);

      this.logger.debug(
        `${Object.keys(playersPerPeerId).length} players joined on set peers`,
      );

      this.emitter.emit(HostPlayP2pClientEventType.PlayersJoined, {
        joinedPlayers: peerIds.map(peerId => ({
          id: peerId,
          username: playersPerPeerId[peerId].username,
        })),
        allPlayers: this.players,
      });
    });
  }

  private async syncGameFromPlayers(
    playersPerPeerId: Record<string, PlayerPeerMetadata>,
  ) {
    const playersOrderedByGameVersion = Object.entries(playersPerPeerId)
      .map(([peerId, metadata]) => ({ peerId, ...metadata }))
      .sort((a, b) => b.gameVersion - a.gameVersion);

    if (playersOrderedByGameVersion.every(p => p.gameVersion === 0)) {
      return;
    }

    let currentPlayerIndex = 0;

    const syncWithPlayer = async () => {
      const player = playersOrderedByGameVersion[currentPlayerIndex];

      await this.sendToPlayersSync([player.peerId], {
        type: PlayP2pHostMessageType.RequestGameState,
        payload: undefined,
      });

      let tries = 0;
      while (this._game.version < player.gameVersion) {
        tries += 1;
        await sleep(100);

        if (tries > 10) {
          this.logger.warn('Could not sync game with player', {
            player,
          });

          currentPlayerIndex += 1;

          await syncWithPlayer();
        }
      }
    };

    await syncWithPlayer();
  }

  protected async syncGameFromPlayer(game: GameState | GameStateCompressed) {
    let decompressedGame: GameState;

    if (!isGameState(game)) {
      decompressedGame = this.gameCompressor.decompress(game);
    } else {
      decompressedGame = game;
    }

    if (decompressedGame.version > 0 && this._game.version < decompressedGame.version) {
      this.setGame(decompressedGame);

      this.emitter.emit(HostPlayP2pClientEventType.GameChanged, decompressedGame);
    }
  }

  private peerDisconnectedTimeoutPid: number | undefined;

  private peerDisconnectionQueue: string[] = [];

  protected onPeerDisconnected(peerId: string) {
    if (this.disposed) return;

    const player = this.players.find(p => p.id === peerId);

    if (!player) {
      return;
    }

    if (this.peerDisconnectedTimeoutPid) {
      clearTimeout(this.peerDisconnectedTimeoutPid);
    }

    this.peerDisconnectionQueue.push(peerId);

    this.peerDisconnectedTimeoutPid = window.setTimeout(() => {
      if (this.peerDisconnectionQueue.length > 1) {
        this.logger.debug(`${this.peerDisconnectionQueue.length} players disconnected`);

        this.emitter.emit(HostPlayP2pClientEventType.PlayersDisconnected, {
          disconnectedPlayers: this.peerDisconnectionQueue,
          allPlayers: this.players,
        });
      } else {
        this.logger.debug(`Player ${player.id} disconnected`);

        this.emitter.emit(HostPlayP2pClientEventType.PlayerDisconnected, {
          disconnectedPlayerId: player.id,
          allPlayers: this.players,
        });
      }

      this.peerDisconnectionQueue = [];

      this.peerDisconnectedTimeoutPid = undefined;
    }, 500);
  }

  private getPlayer(playerId: string): PlayerData | undefined {
    return this.players.find(p => p.id === playerId);
  }

  protected onWebsocketMessage(_: PlayWebsocketServerToClientMessage) {
    // Nothing to do here
  }

  protected async onPeerMessage(peerId: string, message: PlayP2pPlayerMessage) {
    if (this.disposed) return;

    const player = this.getPlayer(peerId);

    switch (message.type) {
      case PlayP2pPlayerMessageType.SendInteractiveQuestionAnswer: {
        if (player) {
          this.onInteractiveQuestionAnswerReceived(player, { answer: message.payload });
        }
        break;
      }
      case PlayP2pPlayerMessageType.SendScoreableInteractiveQuestionAnswer: {
        if (player) {
          this.onInteractiveQuestionAnswerReceived(player, message.payload);
        }
        break;
      }
      case PlayP2pPlayerMessageType.SendGameState: {
        this.syncGameFromPlayer(message.payload.game);
        break;
      }
      case PlayP2pPlayerMessageType.RequestGameState: {
        await this.sendGameStateToPlayers([peerId]);
        break;
      }
      case PlayP2pPlayerMessageType.Benchmark: {
        runInAction(() => {
          if (
            !this.lastBenchmarkData ||
            this.lastBenchmarkData.id !== message.payload.id
          ) {
            return;
          }

          const messageInKb = calculateStringSizeInKb(message.payload.message);

          this.lastBenchmarkData.amountOfMessagesReceived += 1;
          this.lastBenchmarkData.kbReceived += messageInKb;

          if (player) {
            const { infoPerPlayer } = this.lastBenchmarkData;

            const playerInfo = infoPerPlayer[player.username] ?? {
              amountOfMessagesReceived: 0,
              maxTime: 0,
              minTime: 0,
              avgTime: 0,
              kbReceived: 0,
            };

            playerInfo.amountOfMessagesReceived += 1;
            playerInfo.kbReceived += messageInKb;

            const time =
              Date.now() - this.lastBenchmarkData.startTime - message.payload.slept;

            playerInfo.maxTime = Math.max(playerInfo.maxTime, time);

            playerInfo.minTime =
              playerInfo.minTime === 0 ? time : Math.min(playerInfo.minTime, time);

            playerInfo.avgTime +=
              (time - playerInfo.avgTime) / playerInfo.amountOfMessagesReceived;

            const totalMessagesReceived = Object.values(infoPerPlayer).reduce(
              (acc, pi) => acc + pi.amountOfMessagesReceived,
              0,
            );

            if (
              totalMessagesReceived === this.lastBenchmarkData.amountOfMessagesExpected
            ) {
              this.logger.debug('Benchmark finished', this.lastBenchmarkData);

              clearTimeout(this.lastBenchmarkData.timeoutPid);

              this.lastBenchmarkData.running = false;
            }

            if (totalMessagesReceived > this.lastBenchmarkData.amountOfMessagesExpected) {
              this.logger.error(
                'Benchmark received more messages than expected',
                this.lastBenchmarkData,
              );
            }
          }
        });

        break;
      }
      default:
        this.logger.error(`Unhandled message`, { message });
        break;
    }
  }

  private handleHostReplaced() {
    this.emitter.emit(HostPlayP2pClientEventType.HostReplaced);

    this.dispose();
  }

  protected onServerConnection() {
    super.onServerConnection();

    this.numRetriesOnSessionIdInUseError = 0;
    this.emitter.emit(HostPlayP2pClientEventType.Joined);
    this.emitter.emit(HostPlayP2pClientEventType.GameSync);
  }

  protected onServerError(code: PlayErrorCodes | BaseErrorCodes): {
    handled: boolean;
  } {
    const { handled } = super.onServerError(code);

    if (handled) return { handled };

    switch (code) {
      case PlayErrorCodes.SessionIdInUse:
        sleep(addJitterToTime(2500)).then(() => {
          if (this.numRetriesOnSessionIdInUseError >= MaxRetriesOnSessionIdInUseError) {
            this.logger.error('SessionId in use. Max number of retries reached...');
            this.disconnectFromServer();
            return;
          }

          this.numRetriesOnSessionIdInUseError += 1;

          this.logger.error('SessionId in use. Trying to reconnect...');
          this.disconnectFromServer();
          this.connectToServer();
        });

        return { handled: true };
      case PlayErrorCodes.CannotReplaceHost:
        this.dispose();
        return { handled: true };
      default:
        return { handled: false };
    }
  }

  protected onServerDisconnection(reason: PlayServerDisconnectReason) {
    switch (reason) {
      case PlayServerDisconnectReason.HostReplaced:
        this.handleHostReplaced();
        break;
      default:
        this.logger.warn('Unhandled server disconnection reason', { reason });
        break;
    }
  }

  public isPlayerConnected(username: string): boolean {
    return this.players.some(p => p.username === username);
  }

  private sendToPlayers(
    playerIds: string[],
    message: Omit<PlayP2pHostMessage, 'gameVersion'>,
  ) {
    playerIds.forEach(playerId => {
      this.sendToPeer(playerId, this.appendGameVersionToMessage(message));
    });
  }

  private async sendToPlayersSync(
    playerIds: string[],
    message: Omit<PlayP2pHostMessage, 'gameVersion'>,
  ) {
    const unsuccessfulPlayers: string[] = [];

    const players = this.getPlayerDataFromPeerIds(playerIds);

    const promises = players.map(player =>
      this.sendToPeerSync(player.id, this.appendGameVersionToMessage(message)),
    );

    const results = await Promise.allSettled(promises);

    results.forEach((result, index) => {
      if (result.status === 'rejected') {
        const player = players[index];
        const e = result.reason;
        const isPeerSyncMessageTimeout = e instanceof PeerSyncMessageTimeoutError;

        if (!isPeerSyncMessageTimeout) {
          this.logger.debug('Error sending message to player, retrying...', {
            player,
            error: decodeError(e),
          });

          unsuccessfulPlayers.push(player.id);
        }
      }
    });

    if (unsuccessfulPlayers.length > 0) {
      await this.sendToPlayersSync(unsuccessfulPlayers, message);
    }
  }

  private sendToAllPlayers(message: Omit<PlayP2pHostMessage, 'gameVersion'>) {
    this.sendBroadcast(this.appendGameVersionToMessage(message));
  }

  protected setConnection(peerId: string, metadata: PlayerPeerMetadata): boolean {
    if (metadata.gameVersion === 0 && this._game.isSessionBlocked) {
      this.sendToPlayers([peerId], {
        type: PlayP2pHostMessageType.SessionBlocked,
        payload: undefined,
      });

      return false;
    }

    if (this.isPlayersLimitReached && !this._connections.get(peerId)) {
      this.logger.debug(`Trying to access to session with players limit reached`, {
        player: {
          username: metadata.username,
          gameVersion: metadata.gameVersion,
        },
      });

      this.sendToPlayers([peerId], {
        type: PlayP2pHostMessageType.PlayersLimitReached,
        payload: undefined,
      });

      return false;
    }

    return super.setConnection(peerId, metadata);
  }

  private async sendGameStateToPlayers(playerIds: string[]) {
    return this.sendToPlayersSync(playerIds, {
      type: PlayP2pHostMessageType.SendGameState,
      payload: {
        game: this.gameCompressor.compress(this.game.asJS),
      },
    });
  }

  private async onUpdateGame(gameChangeFn?: (game: GameStateModel) => void) {
    gameChangeFn?.(this.game);

    this.emitter.emit(HostPlayP2pClientEventType.GameChanged, this._game);
  }

  public startGame() {
    this.onUpdateGame(m => m.setIsStarted(true));

    this.logger.debug('Sent start game message');
  }

  public kickPlayer(playerId: string) {
    this.sendToPlayers([playerId], {
      type: PlayP2pHostMessageType.KickPlayer,
      payload: undefined,
    });

    this.logger.debug('Sent kick player message', { playerId });
  }

  public toggleOpenAnswerVisibility(username: string, interactiveQuestionId: string) {
    const interactiveQuestion = this.game.toggleOpenAnswerVisibility(
      interactiveQuestionId,
      username,
    );

    if (!interactiveQuestion) {
      return false;
    }

    this.sendToAllPlayers({
      type: PlayP2pHostMessageType.ToggleOpenAnswerVisibility,
      payload: {
        interactiveQuestionId,
        userAlias: username,
        isHidden: interactiveQuestion.answer.isHidden,
      },
    });

    this.onUpdateGame();

    this.emitter.emit(HostPlayP2pClientEventType.ToggleOpenAnswerVisibility, {
      interactiveQuestionId,
      username,
    });

    this.logger.debug('Sent openAnswer visibility toggle message', {
      interactiveQuestionId,
      username,
    });

    return interactiveQuestion.answer.isHidden;
  }

  public sendChangeSlide(slideId: string) {
    this.onUpdateGame(m => m.setCurrentSlide(slideId));

    this.logger.debug('Sent change slide message', { slideId });
  }

  public onInteractiveQuestionAnswerReceived(
    player: PlayerData,
    payload:
      | { answer: PlayerSendInteractiveQuestionAnswerMessage['payload'] }
      | PlayerSendScoreableInteractiveQuestionAnswerMessage['payload'],
  ) {
    this.logger.debug('Interactive question answer received', { player, payload });

    const answers = this.game.getInteractiveQuestionAnswer(
      payload.answer.interactiveQuestionId,
    );

    const playerAlreadyAnswered = !!answers?.playerUserAnswers.find(
      a => a.player.username === player.username,
    );

    if (playerAlreadyAnswered) {
      return;
    }

    this.onUpdateGame(m => m.addInteractiveQuestionAnswer(player, payload));

    this.emitter.emit(HostPlayP2pClientEventType.InteractiveQuestionAnswerReceived);
  }

  async resolveInteractiveQuestion(
    interactiveQuestionId: string,
    isLastScored?: boolean,
  ) {
    this.game.resolveInteractiveQuestion(interactiveQuestionId);

    this.sendToAllPlayers({
      type: PlayP2pHostMessageType.InteractiveQuestionResolved,
      payload: {
        interactiveQuestionId,
        isLastScored,
      },
    });

    this.onUpdateGame();

    this.emitter.emit(HostPlayP2pClientEventType.InteractiveQuestionResolved, {
      interactiveQuestionId,
      isLastScored,
    });

    this.logger.debug('Sent interactive question resolved message', {
      interactiveQuestionId,
    });
  }

  private getPlayerDataFromPeerIds(peerIds: string[]): PlayerData[] {
    return peerIds.map(id => this.getPlayer(id)).filter(Boolean) as PlayerData[];
  }

  @observable
  public lastBenchmarkData:
    | {
        id: string;
        messageSizeInKb: number;

        reason: 'ping' | 'benchmark';

        amountOfMessagesSent: number;
        amountOfMessagesExpected: number;
        amountOfMessagesReceived: number;

        kbReceived: number;

        infoPerPlayer: Record<
          string,
          {
            amountOfMessagesReceived: number;
            maxTime: number;
            minTime: number;
            avgTime: number;
            kbReceived: number;
          }
        >;

        running: boolean;
        startTime: number;

        timeoutPid: number;
      }
    | undefined;

  @action
  public stopBenchmark() {
    this.lastBenchmarkData = undefined;
  }

  @action
  public benchmark(numMessages: number, messageSizeInKb: number, isPingMessage = false) {
    if (messageSizeInKb <= 0) {
      console.error('Message size must be greater than 0');
      return;
    }

    if (numMessages <= 0) {
      console.error('Number of messages must be greater than 0');
      return;
    }

    if (this.lastBenchmarkData && this.lastBenchmarkData.running) {
      console.error(
        `Benchmark already running, timeout in ${Math.abs(this.lastBenchmarkData.startTime + 60000 - Date.now())}ms`,
      );
      return;
    }

    this.lastBenchmarkData = undefined;

    // Random message to send with random characters
    const message = calculate(() => {
      const characters = 'ABCDEFGHIJKLMOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

      let result = '';

      while (result.length < messageSizeInKb * 1024) {
        const randomCharacter = characters.charAt(
          Math.floor(Math.random() * characters.length),
        );

        const randomLength = Math.floor(Math.random() * 100);

        result += randomCharacter.repeat(randomLength);
      }

      return result;
    });

    const id = nanoid();

    this.sendToAllPlayers({
      type: PlayP2pHostMessageType.Benchmark,
      payload: {
        id,
        numMessages,
        message,
        sleepBetweenMessages: !isPingMessage,
      },
    });

    if (isPingMessage && this.lastBenchmarkData !== undefined) {
      return;
    }

    this.lastBenchmarkData = {
      id,
      messageSizeInKb,
      reason: isPingMessage ? 'ping' : 'benchmark',
      kbReceived: 0,
      amountOfMessagesSent: numMessages,
      amountOfMessagesReceived: 0,
      amountOfMessagesExpected: numMessages * this.players.length,
      infoPerPlayer: this.players.reduce((acc, player) => {
        acc[player.username] = {
          amountOfMessagesReceived: 0,
          maxTime: 0,
          minTime: 0,
          avgTime: 0,
          kbReceived: 0,
        };

        return acc;
      }, {} as any),
      running: true,
      timeoutPid: window.setTimeout(() => {
        if (this.lastBenchmarkData) {
          this.lastBenchmarkData.running = false;
        }

        console.log('Benchmarking stop due timeout', this.lastBenchmarkData);
      }, 60000),
      startTime: Date.now(),
    };

    console.log('Benchmarking start', this.lastBenchmarkData);
  }

  public resetGame() {
    this.onUpdateGame(m => m.reset());

    this.emitter.emit(HostPlayP2pClientEventType.GameChanged, this._game);
  }

  public setIsSessionBlocked(isSessionBlocked: boolean) {
    this.onUpdateGame(m => m.setIsSessionBlocked(isSessionBlocked));
  }

  public async finishGame() {
    if (this._game.isFinished) return;

    await this.onUpdateGame(m => m.setIsFinished(true));

    this.logger.debug('Sent finish game message');
  }

  dispose() {
    if (this.sendGameStatePatchesIntervalPid) {
      this.sendGameStatePatches();
      clearInterval(this.sendGameStatePatchesIntervalPid);
    }

    if (this.handlePendingPlayersToJoinIntervalPid) {
      clearTimeout(this.handlePendingPlayersToJoinIntervalPid);
    }

    super.dispose();
  }
}
